import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@hooks/useTranslation';
import { WorkpayApiError, isForbiddenError } from '@lib/apiEnvelope';
import { useAuditEvents } from '@hooks/useAuditEvents';
import type { AuditEvent, AuditFilter, FieldChange } from '@/types/audit';
import { AuditEventRow } from './AuditEventRow';
import { FormSchemaPreview } from './FormSchemaPreview';
import styles from './AuditTrailModal.module.scss';

/**
 * Optional resolver consumers can pass to translate opaque item keys
 * (e.g. permission UUIDs) to human-readable labels in array-diff and
 * `REFERENCE`-typed renders.
 *
 * The optional `referenceType` discriminator (e.g. `'ROLE'`, `'PERMISSION'`,
 * `'USER'`) lets a single resolver dispatch across catalogs for events that
 * mix references — see audit-wire-shape-v2.
 *
 * Existing single-argument resolvers (`(key) => …`) continue to work because
 * `referenceType` is optional and JS ignores extra positional args.
 *
 * Return `null` to fall back to the default display (truncated UUID etc.).
 */
export type ResolveItemLabel = (key: string, referenceType?: string) => string | null;

export interface AuditTrailModalProps {
  open: boolean;
  onClose: () => void;
  filter: AuditFilter;
  resolveItemLabel?: ResolveItemLabel;
  /**
   * Optional context label rendered in the filter bar (left of the dropdown).
   * Typically the name of the resource whose audit trail is being viewed
   * (e.g. a role name, user's full name, tenant name).
   */
  subject?: string;
}

/**
 * Time-window filter — translates to a server-side `fromDate` query param
 * (RFC3339). `all` means no constraint.
 */
type TimeWindow = 'all' | 'this_week' | 'this_month' | 'this_year';

const FILTER_OPTIONS: ReadonlyArray<{
  value: TimeWindow;
  labelKey: string;
  fallback: string;
}> = [
  { value: 'all', labelKey: 'K_AUDIT_FILTER_ALL', fallback: 'All' },
  { value: 'this_week', labelKey: 'K_AUDIT_FILTER_THIS_WEEK', fallback: 'This week' },
  { value: 'this_month', labelKey: 'K_AUDIT_FILTER_THIS_MONTH', fallback: 'This month' },
  { value: 'this_year', labelKey: 'K_AUDIT_FILTER_THIS_YEAR', fallback: 'This year' },
];

/**
 * RFC3339 `fromDate` for a given window, or undefined for `all`.
 *  - this_week: Monday of current week, 00:00 local
 *  - this_month: 1st of current month, 00:00 local
 *  - this_year: Jan 1 of current year, 00:00 local
 */
function fromDateForWindow(window: TimeWindow): string | undefined {
  if (window === 'all') return undefined;
  const now = new Date();
  let start: Date;
  switch (window) {
    case 'this_week': {
      start = new Date(now);
      const day = start.getDay(); // 0 = Sunday
      const offset = day === 0 ? 6 : day - 1; // ISO week starts Monday
      start.setDate(start.getDate() - offset);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'this_year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
  }
  return start.toISOString();
}

export function AuditTrailModal({
  open,
  onClose,
  filter,
  resolveItemLabel,
  subject,
}: AuditTrailModalProps) {
  const { t } = useTranslation();
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('all');

  const [previewState, setPreviewState] = useState<{
    event: AuditEvent;
    change: FieldChange;
    side: 'before' | 'after';
  } | null>(null);

  const auditScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollRef = useRef<number>(0);

  // Restore scroll position when returning from preview to list.
  useLayoutEffect(() => {
    if (previewState === null && auditScrollRef.current) {
      auditScrollRef.current.scrollTop = savedScrollRef.current;
    }
  }, [previewState]);

  // Esc in preview returns to list; capture-phase + stopPropagation prevents
  // the shared Modal's Esc handler from closing the whole modal.
  useEffect(() => {
    if (previewState === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setPreviewState(null);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [previewState]);

  // Reset on close so the next audit doesn't inherit the prior session's
  // preview or time-window choice.
  useEffect(() => {
    if (!open) {
      setPreviewState(null);
      setTimeWindow('all');
    }
  }, [open]);

  const handlePreviewForm = useCallback(
    (args: { event: AuditEvent; change: FieldChange; side: 'before' | 'after' }) => {
      savedScrollRef.current = auditScrollRef.current?.scrollTop ?? 0;
      setPreviewState(args);
    },
    [],
  );

  const handleToggleSide = useCallback(() => {
    setPreviewState((s) =>
      s ? { ...s, side: s.side === 'before' ? 'after' : 'before' } : s,
    );
  }, []);

  const handlePreviewBack = useCallback(() => {
    setPreviewState(null);
  }, []);

  // Merge the host filter with the time-window's `fromDate`. The query key
  // (filterToKey) reflects fromDate, so changing the dropdown re-fires the
  // query against the server with the new window.
  const effectiveFilter = useMemo<AuditFilter>(() => {
    const fromDate = fromDateForWindow(timeWindow);
    if (fromDate === undefined) return filter;
    return { ...filter, fromDate };
  }, [filter, timeWindow]);

  const query = useAuditEvents(effectiveFilter, open);

  // Server-side filter via `fromDate` — no client-side narrowing needed.
  const visibleEvents = useMemo<AuditEvent[]>(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((p) => p.data);
  }, [query.data]);

  const error: WorkpayApiError | null = query.error
    ? (query.error as WorkpayApiError)
    : null;
  const isInitialError = !!error && !query.data;
  const isPaginationError = !!error && !!query.data && query.data.pages.length > 0;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t('K_AUDIT_TITLE', 'Audit trail')}
      size="xl"
    >
      <div className={styles.body}>
        {previewState !== null ? (
          <div className={styles.swapFade} key="preview">
            <FormSchemaPreview
              event={previewState.event}
              change={previewState.change}
              side={previewState.side}
              subject={subject}
              onBack={handlePreviewBack}
              onToggleSide={handleToggleSide}
            />
          </div>
        ) : (
          <div className={styles.swapFade} ref={auditScrollRef}>
            <div className={styles.filterBar}>
              {subject && <span className={styles.subject}>{subject}</span>}
              <select
                className={styles.filter}
                value={timeWindow}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setTimeWindow(e.target.value as TimeWindow)
                }
                aria-label={t('K_AUDIT_FILTER_ALL', 'All')}
              >
                {FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.fallback)}
                  </option>
                ))}
              </select>
            </div>

            {isInitialError && (
              <ErrorBlock error={error} t={t} onRetry={() => void query.refetch()} />
            )}

            {!isInitialError && query.isPending && <SkeletonRows />}

            {!isInitialError && !query.isPending && visibleEvents.length === 0 && (
              <EmptyState filtered={timeWindow !== 'all'} t={t} />
            )}

            {visibleEvents.length > 0 && (
              <ul className={styles.list}>
                {visibleEvents.map((event) => (
                  <AuditEventRow
                    key={event.id}
                    event={event}
                    resolveItemLabel={resolveItemLabel}
                    onPreviewForm={handlePreviewForm}
                  />
                ))}
              </ul>
            )}

            {query.hasNextPage && (
              <div className={styles.loadMoreWrap}>
                {isPaginationError && (
                  <div className={styles.paginationError}>
                    {t('K_AUDIT_ERROR_GENERIC', "Couldn't load audit events. Try again.")}
                  </div>
                )}
                <button
                  type="button"
                  className={styles.loadMore}
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage
                    ? t('K_AUDIT_LOADING', 'Loading audit events…')
                    : t('K_AUDIT_LOAD_MORE', 'Load more')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function ErrorBlock({
  error,
  t,
  onRetry,
}: {
  error: WorkpayApiError;
  t: (k: string, fb?: string) => string;
  onRetry: () => void;
}) {
  if (isForbiddenError(error)) {
    return (
      <div className={styles.errorBlock}>
        <p>{t('K_AUDIT_ERROR_FORBIDDEN', "You don't have access to audit events.")}</p>
      </div>
    );
  }
  const prefix = t('K_AUDIT_ERROR_PREFIX', "Couldn't load audit events");
  const msg = error.description
    ? `${prefix}: ${error.description}`
    : t('K_AUDIT_ERROR_GENERIC', "Couldn't load audit events. Try again.");
  return (
    <div className={styles.errorBlock}>
      <p>{msg}</p>
      <button type="button" className={styles.retryButton} onClick={onRetry}>
        {t('K_BUTTON_RETRY', 'Retry')}
      </button>
    </div>
  );
}

function EmptyState({
  filtered,
  t,
}: {
  filtered: boolean;
  t: (k: string, fb?: string) => string;
}) {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon} aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M20 12v8l5 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p>
        {filtered
          ? t('K_AUDIT_EMPTY_FILTERED', 'No matching events. Try loading more.')
          : t('K_AUDIT_EMPTY', 'No audit events yet.')}
      </p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <ul className={styles.list} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className={styles.skeletonRow}>
          <div className={styles.skeletonTime} />
          <div className={styles.skeletonActor} />
          <div className={styles.skeletonUpdates}>
            <div className={styles.skeletonBarShort} />
            <div className={styles.skeletonBarLong} />
          </div>
        </li>
      ))}
    </ul>
  );
}
